/**
 * b2b-moq-qty.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Handles MOQ (Minimum Order Quantity) enforcement in two scenarios:
 *
 * 1. PRODUCT PAGE (PDP)
 *    When the variant changes, updates the quantity input value/min/step to
 *    match the new variant's quantity_rule.min.
 *    (Initial value is already set correctly by Liquid in the template.)
 *
 * 2. GRID / COLLECTION / HOME PAGE CARDS
 *    Before any .form-btn-item-product form submits, ensures the hidden
 *    quantity input value is at least the variant's MOQ.
 *
 * Standalone — does NOT require changes to any core theme files.
 * ─────────────────────────────────────────────────────────────────────────────
 */

(function () {
  'use strict';

  /* ══════════════════════════════════════════════════════════════════════════
     SHARED UTILITY
  ══════════════════════════════════════════════════════════════════════════ */

  function getQtyRule(variants, variantId) {
    const id = String(variantId);
    const v  = (variants || []).find(function (x) { return String(x.id) === id; });
    if (v && v.quantity_rule) return v.quantity_rule;
    return { min: 1, max: null, increment: 1 };
  }


  /* ══════════════════════════════════════════════════════════════════════════
     PART 1 — PRODUCT PAGE: update inputs on variant change
     (Initial values are already correct from Liquid; JS only handles changes.)
  ══════════════════════════════════════════════════════════════════════════ */

  function applyMoqToInputs(rule) {
    if (!rule) return;
    const min  = parseInt(rule.min, 10)       || 1;
    const step = parseInt(rule.increment, 10) || 1;

    // All visible qty number inputs (main form + sticky bar)
    document.querySelectorAll(
      'input.quantity-selector, input[name="quantity"][type="number"]'
    ).forEach(function (input) {
      input.min  = min;
      input.step = step;
      input.setAttribute('data-moq', min);
      const currentVal = parseInt(input.value, 10);
      if (isNaN(currentVal) || currentVal < min) {
        input.value = min;
      } else {
        // Align to step
        const aligned = Math.ceil((currentVal - min) / step) * step + min;
        if (currentVal !== aligned) input.value = aligned;
      }
    });

    // Update MOQ label
    const moqLabel = document.querySelector('.b2b-moq-label');
    if (moqLabel) {
      if (min > 1) {
        moqLabel.textContent = '(Min Order Qty: ' + min + ')';
        moqLabel.style.display = '';
      } else {
        moqLabel.style.display = 'none';
      }
    }
  }

  function initPDP() {
    // Read variants from the JSON block we embed in product-single-addtocart.liquid
    // Falls back to the product-variant-swatch JSON block if present
    let variants = [];
    const jsonBlock = document.getElementById('b2b-variants-json') ||
      document.querySelector(
        'product-variant-swatch script[type="application/json"],' +
        'product-variant-dropdown script[type="application/json"]'
      );

    if (jsonBlock) {
      try { variants = JSON.parse(jsonBlock.textContent); } catch (e) { /* ignore */ }
    }

    if (!variants.length) return; // Not a PDP or no data available

    // Watch for variant changes via the hidden id input
    document.addEventListener('change', function (e) {
      const t = e.target;
      if (
        (t.name === 'id' || t.id === 'variant_id') &&
        (t.type === 'hidden')
      ) {
        applyMoqToInputs(getQtyRule(variants, t.value));
      }
    });

    // Browser back/forward
    window.addEventListener('popstate', function () {
      const urlId = new URLSearchParams(window.location.search).get('variant');
      if (urlId) applyMoqToInputs(getQtyRule(variants, urlId));
    });
  }


  /* ══════════════════════════════════════════════════════════════════════════
     PART 2 — GRID / COLLECTION / HOME PAGE: set MOQ before form submit
  ══════════════════════════════════════════════════════════════════════════ */

  function initGridMOQ() {
    // Capture phase so we fire before the theme's submit handler
    document.addEventListener('submit', function (e) {
      const form = e.target;
      if (!form.classList.contains('form-btn-item-product')) return;

      // Get variant data from the card wrapper
      const card = form.closest('[data-json-product]');
      if (!card) return;

      let variants = [];
      try {
        const raw = card.getAttribute('data-json-product').replace(/&quot;/g, '"');
        const parsed = JSON.parse(raw);
        variants = parsed.variants || [];
      } catch (err) {
        return;
      }

      const idInput = form.querySelector('input[name="id"]');
      if (!idInput || !variants.length) return;

      const rule = getQtyRule(variants, idInput.value);
      const min  = parseInt(rule.min, 10) || 1;
      if (min <= 1) return;

      let qtyInput = form.querySelector('input[name="quantity"]');
      if (!qtyInput) {
        qtyInput = document.createElement('input');
        qtyInput.type = 'hidden';
        qtyInput.name = 'quantity';
        form.appendChild(qtyInput);
      }

      if (isNaN(parseInt(qtyInput.value, 10)) || parseInt(qtyInput.value, 10) < min) {
        qtyInput.value = min;
      }
    }, true);
  }


  /* ══════════════════════════════════════════════════════════════════════════
     PART 3 — Clamp qty input to MOQ after theme's +/- buttons change value.
     We do NOT intercept clicks (that caused double-step); we just correct
     the value if it falls below MOQ after the theme has already updated it.
  ══════════════════════════════════════════════════════════════════════════ */

  function initQtyClamp() {
    // Listen in bubble phase — fires after the theme's click handler sets the value
    document.addEventListener('input', function (e) {
      const input = e.target;
      if (!input.matches('input.quantity-selector, input[name="quantity"][type="number"]')) return;
      const moq = parseInt(input.getAttribute('data-moq'), 10) || 1;
      if (parseInt(input.value, 10) < moq) {
        input.value = moq;
      }
    });

    document.addEventListener('change', function (e) {
      const input = e.target;
      if (!input.matches('input.quantity-selector, input[name="quantity"][type="number"]')) return;
      const moq = parseInt(input.getAttribute('data-moq'), 10) || 1;
      if (parseInt(input.value, 10) < moq) {
        input.value = moq;
      }
    });
  }


  /* ══════════════════════════════════════════════════════════════════════════
     INIT
  ══════════════════════════════════════════════════════════════════════════ */

  function init() {
    initPDP();
    initGridMOQ();
    initQtyClamp();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
